import { Button, Dropdown, Tab, Tabs } from 'react-bootstrap';
import portfolioApplicationsSignal from 'signals/EdoPortfolio.signal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  handleDownloadApplicationsData,
  handleEdoPortfolioAppliedFiltersChange,
  handleEdoPortfolioFilterChange,
  resetEdoPortfolioFilterInputs,
  setAndFetchEdoApplications,
} from './EdoPortfolio.helpers';
import { Link, useHistory, useLocation } from 'react-router-dom';
import ApplicationDetailModal from 'components/global/ApplicationDetail/ApplicationDetailModal';
import SimpleTable from '../../../global/SimpleTable';
import dateOptions from '../../../global/Constant/dateOptions';
import currencyOptions from '../../../global/Constant/currencyOptions';
import SimpleTableFilterDropdown from 'components/global/SimpleTableFilterDropdown';
import { enumCaseToTabCase, titleCaseString } from '../../../../libs/string';
import {
  getStatusStyles,
  handleTabChange,
} from 'libs/functions/global.functions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { watchlistIcon } from 'components/global/Constant/Icons';
import BreadCrumbWrapper from 'components/global/BreadCrumbs/BreadCrumbs';
import PaginationItems from 'components/global/Pagination';
import capitalizeFirst from 'utils/capitalizeFirst';
import { portfolioTableDropdownPopperConfig } from 'components/global/Applications/portfolioTableDropdownPopperConfig';
import SendTechnicalAssistanceModal from '../EdoTechnicalAssistance/components/SendTechnicalAssistanceModal';
import { handleSendTechnicalAssistanceClick } from '../EdoTechnicalAssistance/edoTechnicalAssistance.helpers';
import $appSettings from 'signals/AppSettings.signal';
import { checkEdoPermissionsAcrossPrograms } from '../../../../libs/edoPermissions';
import userAccountSignal from 'signals/UserAccount.signal';
import ChooseApplicationDraftModal from 'components/global/SubmitApplications/components/ChooseApplicationDraftModal';
import { terminateApplicationDraft } from 'components/global/SubmitApplications/SubmitApplications.helpers';
import { applicationDraftSignal } from 'signals/SubmitApplication.signal';
import EdoClaimsTracking from './EdoClaimsTracking';

const HEADERS = [
  {
    key: 'status',
    title: 'Status',
    sortable: true,
  },
  {
    key: 'createdDate',
    title: 'Date Submitted',
  },
  {
    key: 'id',
    title: 'ID',
    sortable: true,
  },
  {
    key: 'programName',
    title: 'Program Name',
    sortable: true,
  },
  {
    key: 'businessName',
    title: 'Business Name',
  },
  {
    key: 'sediBusiness',
    title: 'SEDI',
    sortable: true,
  },
  {
    key: 'contactName',
    title: 'Contact Name',
  },
  {
    key: 'askedAndApprovedAmount',
    title: 'Enrolled / Total Amount',
    options: { isCurrency: true },
  },
  {
    key: 'action',
    title: 'Action',
  },
];

const Table = ({ currentPage, canInsert, draftUiTick = 0, onAfterDraftMutation }) => {
  void draftUiTick;
  const { dropdownFilters, appliedFilters, applications, bundle, totalPages } =
    portfolioApplicationsSignal.value;
  const draftCount = applicationDraftSignal.value.applicationDrafts.length;
  const { APPLICATION_STATUS } = $appSettings.value.constants;

  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const scopedBusinessId = searchParams.get('businessId');
  const scopedLenderId = searchParams.get('lenderId');
  const hasScopedContext = !!scopedBusinessId || !!scopedLenderId;

  const clearScopedContext = () => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete('businessId');
    nextParams.delete('lenderId');
    nextParams.delete('page');

    const { businessId: _b, lenderId: _l, ...restApplied } = appliedFilters || {};
    handleEdoPortfolioAppliedFiltersChange(restApplied);

    const search = nextParams.toString();
    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : '',
    });
  };

  return (
    <div className="bg-white p-32 border border-gray shadow-sm">
      <div className="d-flex flex-row justify-content-between mb-16">
        <div className="d-flex align-items-center">
          <SimpleTableFilterDropdown
            filtersState={dropdownFilters}
            onChange={({ name, value }) =>
              handleEdoPortfolioFilterChange(name, value)
            }
            onApply={(filters) => {
              handleEdoPortfolioAppliedFiltersChange(filters);
            }}
            onClearFilterInputs={resetEdoPortfolioFilterInputs}
            filters={[
              {
                type: 'select',
                label: 'Status',
                key: 'status',
                options: Object.entries(APPLICATION_STATUS).map(([, value]) => ({
                  label:
                    value === APPLICATION_STATUS.inProgress
                      ? 'DRAFT'
                      : titleCaseString(value),
                  value,
                })),
              },
              {
                type: 'integer',
                label: 'ID',
                key: 'id',
              },
              {
                type: 'text',
                label: 'CalCAP Loan Number',
                key: 'calcapLoanNumber',
              },
              {
                type: 'integer',
                label: 'CSP App #',
                key: 'cspAppNumber',
              },
              {
                type: 'select',
                label: 'Program Name',
                key: 'programName',
                options: bundle.fundingPrograms?.map((fundingProgram) => ({
                  label: fundingProgram.name,
                  value: fundingProgram.id,
                })),
              },
              {
                type: 'select',
                label: 'SEDI',
                key: 'sediBusiness',
                options: [
                  {
                    label: 'Yes',
                    value: 'Yes',
                  },
                  {
                    label: 'No',
                    value: 'No',
                  },
                ],
              },
              {
                type: 'checkbox',
                label: 'Veteran-owned',
                key: 'veteranOwned',
              },
              {
                type: 'checkbox',
                label: 'Woman-owned',
                key: 'womanOwned',
              },
              {
                type: 'checkbox',
                label: 'Minority-owned',
                key: 'minorityOwned',
              },
            ]}
          />
          {hasScopedContext && (
            <div className="btn-group ml-2" role="group" aria-label="Scoped Applications Filter">
              <Button
                type="button"
                variant="outline-secondary"
                disabled
              >
                Scoped by{' '}
                {scopedBusinessId ? `Business (${scopedBusinessId})` : `Lender (${scopedLenderId})`}
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                onClick={clearScopedContext}
              >
                Clear Scope
              </Button>
            </div>
          )}
        </div>
        <div>
          <Button
            variant="outline-secondary"
            className="rounded mr-16"
            onClick={handleDownloadApplicationsData}
          >
            Download
          </Button>
          <Button
            className="rounded mr-16"
            variant="outline-primary"
            disabled={draftCount === 0}
            onClick={() => {
              applicationDraftSignal.update({
                showDraftModal: true,
                fromEdoPortfolio: true,
                draftModalState: 'chooseDraft',
              });
            }}
          >
            Manage Drafts
          </Button>
          {canInsert && (
            <>
              {/*
              <Link
                className="rounded btn btn-outline-info mr-16"
                to={`${location.pathname}/bulk-import`}
              >
                Bulk Import
              </Link>
              */}
              <Link
                className="rounded btn btn-primary"
                to="/edo/portfolio/submit-applications"
              >
                New Application
              </Link>
            </>
          )}
        </div>
      </div>
      <div className="extend-table-container no-padding">
        <SimpleTable
          headers={HEADERS}
          // onClick={(index) => {
          // const application = applications[index];
          // showApplicationDetailsModal(
          //   txApplicationToApplicationDetailsModal(
          //     application,
          //     !!application.ventureCapitalFirm
          //   )
          // );
          onSort={(sort) => {
            handleTabChange(sort);
            setAndFetchEdoApplications(currentPage);
          }}
          rows={applications.map((application) => ({
            status: () => (
              <td className={`${getStatusStyles(application.status).class}`}>
                <div className="d-flex justify-content-between align-items-center">
                  <p className="mb-0">
                    {application.status === APPLICATION_STATUS.inProgress
                      ? 'Draft'
                      : capitalizeFirst(application.status.replace(/_/g, ' '))}
                  </p>
                  <FontAwesomeIcon
                    className={
                      application.watchables?.length
                        ? 'text-right ml-16'
                        : 'd-none'
                    }
                    icon={watchlistIcon}
                  />
                </div>
              </td>
            ),
            createdDate: (application.receivedDate ?? application.createdDate)
              ? new Date(application.receivedDate ?? application.createdDate).toLocaleString(
                  'en-US',
                  dateOptions.date
                )
              : '—',
            id: application.id,
            programName: application.fundingProgram?.name || '',
            businessName: application.business?.name,
            sediBusiness: application.sediBusiness ? 'Yes' : 'No',
            contactName: application.business?.contactName,
            askedAndApprovedAmount: `${application.approvedAmount
              ? application.approvedAmount?.toLocaleString('en-US', currencyOptions.currencyUS)
              : '-'
            } / ${application.askedAmount?.toLocaleString('en-US', currencyOptions.currencyUS) ?? '-'
            }`,
            action: ({ key }) => (
              <td key={key} className="text-right">
                <div className="d-flex justify-content-end">
                  {application.status === APPLICATION_STATUS.inProgress && application.submittedByEdo ? (
                    <Dropdown>
                      <Dropdown.Toggle size="sm" variant="outline-dark">
                        Actions
                      </Dropdown.Toggle>
                      <Dropdown.Menu popperConfig={portfolioTableDropdownPopperConfig}>
                        <Dropdown.Item
                          onClick={() => {
                            history.push(`/edo/portfolio/${application.id}`);
                          }}
                        >
                          Details
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            applicationDraftSignal.update({
                              draftPendingRequest: application,
                            });
                            history.push(
                              `/edo/portfolio/submit-applications?draftId=${application.id}`
                            );
                          }}
                        >
                          Continue
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            if (!window.confirm('Are you sure you want to terminate this draft application? This action cannot be undone.')) return;
                            terminateApplicationDraft(application, onAfterDraftMutation);
                          }}
                        >
                          Terminate
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : application.status === APPLICATION_STATUS.denied ? (
                    <Dropdown>
                      <Dropdown.Toggle size="sm" variant="outline-dark">
                        Actions
                      </Dropdown.Toggle>
                      <Dropdown.Menu popperConfig={portfolioTableDropdownPopperConfig}>
                        <Dropdown.Item
                          onClick={() => {
                            history.push(`/edo/portfolio/${application.id}`);
                          }}
                        >
                          Detail
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() =>
                            handleSendTechnicalAssistanceClick(
                              application.id,
                              application.technicalAssistanceId
                            )
                          }
                        >
                          Send Technical Assistance
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : (
                    <Button
                      className="rounded"
                      size="sm"
                      variant="outline-dark"
                      onClick={() => {
                        history.push(`/edo/portfolio/${application.id}`);
                      }}
                    >
                      Detail
                    </Button>
                  )}
                </div>
              </td>
            ),
          }))}
        />
      </div>
      {totalPages > 1 && (
        <PaginationItems
          className="justify-content-center mt-24"
          totalPages={totalPages}
        />
      )}
    </div>
  );
};

const Portfolio = () => {
  const location = useLocation();
  const history = useHistory();
  const { APPLICATION_STATUS } = $appSettings.value.constants;
  const bundle = portfolioApplicationsSignal.value.bundle;
  const appliedFilters = portfolioApplicationsSignal.value.appliedFilters || {};
  const { tally, numWatchlist, fundingPrograms } = bundle;
  const searchParams = Object.fromEntries(new URLSearchParams(location.search));
  const trackingTab =
    searchParams.trackingTab === 'claims' ? 'claims' : 'applications';
  const activeKey =
    searchParams.status ||
    (appliedFilters.watchables
      ? 'watchlist'
      : appliedFilters.status || 'all');
  const currentPage = searchParams.page ? Number(searchParams.page) : 1;
  const [canView, setCanView] = useState(false);
  const [canInsert, setCanInsert] = useState(false);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);
  const [draftUiTick, setDraftUiTick] = useState(0);

  const refreshPortfolioAndDraftUi = useCallback(async () => {
    await setAndFetchEdoApplications(currentPage);
    setDraftUiTick((t) => t + 1);
  }, [currentPage]);

  const fundingProgramIds = useMemo(
    () => (fundingPrograms || []).map((p) => p.id).sort().join(','),
    [fundingPrograms]
  );

  useEffect(() => {
    const checkPermissions = async () => {
      setIsCheckingPermissions(true);
      const permissions = await checkEdoPermissionsAcrossPrograms(
        fundingPrograms,
        ['canView', 'canInsert']
      );
      setCanView(permissions.canView);
      setCanInsert(permissions.canInsert);
      setIsCheckingPermissions(false);
    };

    checkPermissions();
  }, [fundingProgramIds, userAccountSignal.value.userData]);

  useEffect(() => {
    if (trackingTab !== 'applications') return;

    const searchParams = Object.fromEntries(new URLSearchParams(location.search));
    const updates = { ...portfolioApplicationsSignal.value.appliedFilters };

    if (searchParams.businessId) {
      updates.businessId = searchParams.businessId;
    } else {
      delete updates.businessId;
    }

    if (searchParams.lenderId) {
      updates.lenderId = searchParams.lenderId;
    } else {
      delete updates.lenderId;
    }

    handleEdoPortfolioAppliedFiltersChange(updates);
  }, [location.search, trackingTab]);

  useEffect(() => {
    if (trackingTab !== 'applications') return;
    setAndFetchEdoApplications(currentPage);
  }, [location.search, trackingTab]);

  const handleTrackingTabSelect = (tab) => {
    const next = new URLSearchParams(location.search);
    if (tab === 'claims') {
      next.set('trackingTab', 'claims');
      next.delete('page');
      next.delete('status');
    } else {
      next.delete('trackingTab');
      next.delete('claimsPage');
    }
    const search = next.toString();
    history.push({
      pathname: location.pathname,
      search: search ? `?${search}` : '',
    });
  };

  // Show loading while checking permissions
  if (isCheckingPermissions) {
    return (
      <BreadCrumbWrapper>
        <div className="bg-white p-32 border border-gray shadow-sm">
          <div className="text-center">Loading...</div>
        </div>
      </BreadCrumbWrapper>
    );
  }

  // Don't render if user doesn't have view permission
  if (!canView) {
    return (
      <BreadCrumbWrapper>
        <div className="bg-white p-32 border border-gray shadow-sm">
          <h3 className="text-center">Access Denied</h3>
          <p className="text-center">
            You do not have permission to view applications.
          </p>
        </div>
      </BreadCrumbWrapper>
    );
  }

  return (
    <BreadCrumbWrapper>
      <SendTechnicalAssistanceModal
        onSendCallback={() => setAndFetchEdoApplications(currentPage)}
      />
      <Tabs
        activeKey={trackingTab}
        className="mb-16"
        mountOnEnter
        onSelect={handleTrackingTabSelect}
      >
        <Tab eventKey="applications" title="Applications">
          <Tabs
            activeKey={activeKey || 'all'}
            defaultActiveKey="all"
            mountOnEnter
            onSelect={(status) => {
              if (status === 'watchlist') {
                handleTabChange(null);
              } else {
                handleTabChange(status);
              }

              const currentApplied = portfolioApplicationsSignal.value.appliedFilters;
              const currentDropdown = portfolioApplicationsSignal.value.dropdownFilters;

              if (status === 'watchlist') {
                handleEdoPortfolioAppliedFiltersChange({
                  ...currentApplied,
                  watchables: true,
                });
              } else {
                const { status: _s, ...restApplied } = currentApplied;
                const newStatus =
                  status === 'all' || !status ? undefined : status;
                const newApplied = {
                  ...restApplied,
                  ...(newStatus && { status: newStatus }),
                  watchables: false,
                };
                handleEdoPortfolioAppliedFiltersChange(newApplied);
                portfolioApplicationsSignal.update({
                  dropdownFilters: {
                    ...currentDropdown,
                    status: newStatus ?? '',
                  },
                });
              }
              setAndFetchEdoApplications(currentPage);
            }}
          >
            <Tab eventKey="all" title={`All\n${bundle.count}`}>
              <Table
                currentPage={currentPage}
                canInsert={canInsert}
                draftUiTick={draftUiTick}
                onAfterDraftMutation={refreshPortfolioAndDraftUi}
              />
            </Tab>
            {tally &&
              Object.values(APPLICATION_STATUS).map((status) => {
                const matchingTally = tally.find((t) => t.status === status);
                return (
                  <Tab
                    key={status}
                    eventKey={status}
                    title={`${
                      status === 'IN_PROGRESS' ? 'Draft' : enumCaseToTabCase(status)
                    } ${matchingTally?._count.status || 0}`}
                  >
                    <Table
                      currentPage={currentPage}
                      canInsert={canInsert}
                      draftUiTick={draftUiTick}
                      onAfterDraftMutation={refreshPortfolioAndDraftUi}
                    />
                  </Tab>
                );
              })}
            <Tab eventKey="watchlist" title={`Watchlist ${numWatchlist || 0}`}>
              <Table
                currentPage={currentPage}
                canInsert={canInsert}
                draftUiTick={draftUiTick}
                onAfterDraftMutation={refreshPortfolioAndDraftUi}
              />
            </Tab>
          </Tabs>
        </Tab>
        <Tab eventKey="claims" title="Claims">
          <EdoClaimsTracking fundingPrograms={fundingPrograms} />
        </Tab>
      </Tabs>
      <ApplicationDetailModal currentTab={activeKey} />
      <ChooseApplicationDraftModal onDraftTerminated={refreshPortfolioAndDraftUi} />
    </BreadCrumbWrapper>
  );
};

export default Portfolio;
